import { memo, useCallback, useState } from 'react';
import { flip, useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { Portal } from '@trinity/chat-components/components/Portal/index.js';
import { Focusable } from '@/components/presentation/Focusable';
import { useSameWidthMiddleware, useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers.js';
import { cx } from '@trinity/chat-components';
import type { ItemProps, ItemsProps, IDropDownItem, Props, SelectionProps } from './interfaces';
import styles from './style.module.css';

const Item = memo(<T extends IDropDownItem>({ onSelect, ...props }: ItemProps<T>) => {
  const handleMouseDown = useCallback((item: T) => () => {
    onSelect(item);
  }, [onSelect]);

  return (
    <div
      className={styles.item}
      onMouseDown={handleMouseDown(props.item)}>
      {props.value}
    </div>
  );
});

const Items = memo(<T extends IDropDownItem>(props: ItemsProps<T>) => {

  return (
    <div className={styles.list}>
      {props.items.map(item =>
        <Item
          item={item}
          key={item.id}
          onSelect={props.onSelect}
          value={props.getItemValue(item)} />,
      )}
    </div>
  );
});

const Selection = <T extends IDropDownItem>({
  classes = {},
  ...props
}: SelectionProps<T>) => {
  const textClass = cx(styles.selectedText, classes.text);
  return (
    <Focusable autoFocus={props.autoFocus} disabled>
      <div
        className={cx(styles.input, {
          [styles.disabled]: props.disabled,
        })}
        tabIndex={0}
        onClick={props.toggleVisibility}>
        <div className={textClass}>
          {props.text}
        </div>
        <div className={styles.arrowDown} />
      </div>
    </Focusable>
  );
};

export const Dropdown = <T extends IDropDownItem>({
  classes = {},
  items = [],
  onSelect,
  placeholder,
  sameWidth = true,
  ...props
}: Props<T>) => {
  const [open, setOpen] = useState(false);

  const toggleVisibility = useCallback(() => {
    if (props.disabled) return;

    setOpen(true);
  }, [props.disabled]);

  const handleSelection = useCallback((data: T) => {
    onSelect(data);
    setOpen(false);
  }, [onSelect, setOpen]);

  const sameWidthMiddleware = useSameWidthMiddleware();
  const zIndexMiddleware = useZIndexMiddleware();

  const middleware = sameWidth
    ? [zIndexMiddleware, sameWidthMiddleware, flip({
      fallbackPlacements: ['top'],
    })]
    : [zIndexMiddleware, flip({
      fallbackPlacements: ['top'],
    })];

  const { refs, floatingStyles, context } = useFloating({
    placement: 'bottom-start',
    middleware,
  });

  const handleBlur = useCallback((e: MouseEvent) => {
    if (context.elements.domReference !== e.target) {
      setOpen(false);
    }
  }, [context.elements.domReference]);

  const selectionTextClassName = cx(classes.text, {
    [styles.placeholder]: !props.text && !!placeholder,
  });

  return (
    <>
      <div
        ref={refs.setReference}
        className={cx(styles.root, classes.root)}>
        <Selection
          autoFocus={props.autoFocus}
          disabled={props.disabled}
          open={open}
          text={props.text || placeholder}
          classes={{ text: selectionTextClassName }}
          toggleVisibility={toggleVisibility} />
      </div>
      {open &&
        <Portal>
          <ClickAwayListener
            mouseEvent='onMouseDown'
            onClickAway={handleBlur}
            touchEvent='onTouchStart'>
            <div
              className={styles.popper}
              ref={refs.setFloating}
              style={floatingStyles}>
              <Items
                getItemValue={props.getItemValue}
                items={items}
                onSelect={handleSelection} />
            </div>
          </ClickAwayListener>
        </Portal>
      }
    </>
  );
};

export default Dropdown;